import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import ForwardIcon from '@mui/icons-material/Forward';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { patient } from '@/api/endpoints';
import { formatDate, formatDateTime } from '@/utils/format';
import type { Referral } from '@/types/medical';

export function PatientReferralsPage() {
  const q = useApiQuery({
    queryKey: ['patient', 'referrals'],
    queryFn:  patient.referrals.list,
  });

  return (
    <>
      <PageHeader title="My referrals" />
      <DataState
        query={q}
        empty={{
          title: 'No referrals on record',
          description: 'A referral is created when your clinician asks a specialist to see you.',
        }}
      >
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((r) => <Row key={r.id} referral={r} />)}
          </Stack>
        )}
      </DataState>
    </>
  );
}

function Row({ referral }: { referral: Referral }) {
  const urgencyColor: Record<Referral['urgency'], 'default' | 'primary' | 'warning'> = {
    routine: 'default', soon: 'primary', urgent: 'warning',
  };
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ForwardIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {referral.reason || 'Referral'}
              </Typography>
              <Chip
                size="small"
                label={referral.urgency}
                color={urgencyColor[referral.urgency]}
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Specialty #{referral.target_specialty}
              {referral.icd_code && ` · ICD ${referral.icd_code}`}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" className="tabular">
              issued {formatDateTime(referral.issued_at)}
            </Typography>
            {referral.valid_until && (
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block' }} className="tabular">
                valid until {formatDate(referral.valid_until)}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
